import { useMemo } from "react";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import { Button, Collapse, Modal, Space, Switch, Typography } from "antd";
import { DeleteOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { GRAPH_COLORS } from "../constants";
import type { PieChartComponentSpec, MetricOption, SubjectEntity, ProcessedWidgetData } from "../../Processing/types";
import { ConfigPanelLayout } from "../shared/ConfigPanelLayout";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import { SlicerTokenInput } from "../shared/SlicerTokenInput";
import { MetricDropdownStep } from "../shared/MetricDropdownStep";
import { DateRangeStep } from "../shared/DateRangeStep";
import { isSectionBindable } from "../../Processing/controllerBindings";
import { useControllers } from "../../Contexts/dashboardHooks";
import { resolveDateRange } from "../../Utils/resolveDateRange";
import { PieSubjectPanel } from "./PieSubjectPanel";
import { PieOptionsPanel } from "./PieOptionsPanel";
import { PiePreviewBuilder } from "./PiePreviewBuilder";
import { PiePreviewPanel } from "./PiePreviewPanel";
import {
  pieReducer,
  createInitialState,
  hydrateFromSpec,
  formStateToSpec,
  type PieFormState,
} from "./pieState";

const { Text } = Typography;
const { Panel } = Collapse;


export function PieConfigurator({
  onSpecChange,
  existingSpec,
  initialFormState,
  metrics,
  getSubjectEntities,
}: ConfiguratorSharedProps<PieChartComponentSpec>) {
  const [state, dispatch] = useConfiguratorSync({
    reducer: pieReducer,
    existingSpec,
    hydrate: hydrateFromSpec,
    createInitial: () => createInitialState(metrics[0]?.id),
    initialState: initialFormState as PieFormState | undefined,
    toSpec: formStateToSpec,
    onSpecChange,
    metrics,
    getSubjectEntities,
  });

  const controllers = useControllers();

  const previewBuilder = useMemo(
    () => new PiePreviewBuilder(state, metrics, getSubjectEntities, controllers),
    [state, metrics, getSubjectEntities, controllers],
  );

  const previewData = useMemo<ProcessedWidgetData | null>(
    () => previewBuilder.build(),
    [previewBuilder],
  );

  const firstMetricName = metrics.find((m) => m.id === state.slices[0]?.selectedMetricId)?.name;
  const displayTitle = state.title || firstMetricName || "";

  const summaryParts = useMemo(() => {
    const ranges = [
      ...new Set(
        state.slices.map((s) => resolveDateRange(s)),
      ),
    ];
    const totalSelected = state.slices.reduce(
      (acc, s) => acc + s.selectedSubjects.length + s.selectedDrills.length,
      0,
    );
    const subjectSummary = totalSelected > 0 ? `${totalSelected} selected` : "Full Team";
    return [
      `${state.slices.length} slice${state.slices.length !== 1 ? "s" : ""}`,
      ranges.join(", "),
      subjectSummary,
    ];
  }, [state.slices]);

  const configuredCount = state.slices.filter((s) => s.selectedMetricId != null).length;

  const form = (
    <>
      {/* Pie-level configuration */}
      <Collapse style={{ borderRadius: 0 }}>
        <Panel
          key="pie-config"
          header={
            <Space size={6}>
              <SettingOutlined />
              <Text strong style={{ fontSize: 13 }}>Pie Configuration</Text>
            </Space>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Space size={8} align="center">
              <Text style={{ fontSize: 12, minWidth: 56 }}>Title</Text>
              <SlicerTokenInput
                size="small"
                placeholder="Auto"
                value={state.title}
                onChange={(title) => dispatch({ type: "SET_TITLE", title })}
              />
            </Space>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Text style={{ fontSize: 12 }}>Donut mode</Text>
              <Switch size="small" checked={state.donutMode} onChange={(v) => dispatch({ type: "SET_DONUT_MODE", value: v })} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Text style={{ fontSize: 12 }}>Show data labels</Text>
              <Switch size="small" checked={state.showDataLabels} onChange={(v) => dispatch({ type: "SET_SHOW_DATA_LABELS", value: v })} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Text style={{ fontSize: 12 }}>Show percentages</Text>
              <Switch size="small" checked={state.showPercentages} onChange={(v) => dispatch({ type: "SET_SHOW_PERCENTAGES", value: v })} />
            </div>
          </div>
        </Panel>
      </Collapse>

      {/* Per-slice accordions */}
      <Collapse
        accordion
        activeKey={state.expandedSliceKey}
        onChange={(key) => dispatch({ type: "SET_EXPANDED_SLICE_KEY", key: Array.isArray(key) ? key[0] : key })}
        style={{ borderRadius: 0, borderTop: "none" }}
      >
        {state.slices.map((slice, index) => {
          const color = GRAPH_COLORS[index % GRAPH_COLORS.length];
          const metricName = metrics.find((m) => m.id === slice.selectedMetricId)?.name;
          const handler = (patch: Partial<typeof slice>) =>
            dispatch({ type: "PATCH_SLICE", index, patch });

          return (
            <Panel
              key={`slice-${slice.queryId}`}
              header={
                <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                  <span
                    style={{
                      width: 9, height: 9, borderRadius: "50%",
                      background: color, flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ fontSize: 13 }}>Slice {index + 1}</Text>
                    {metricName && (
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                        {metricName}
                      </Text>
                    )}
                  </div>
                </div>
              }
              extra={
                state.slices.length > 1 ? (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      Modal.confirm({
                        title: "Remove slice?",
                        content: metricName
                          ? `"${metricName}" will be removed from the chart.`
                          : "This slice will be removed from the chart.",
                        okText: "Remove",
                        okButtonProps: { danger: true },
                        onOk: () => dispatch({ type: "REMOVE_SLICE", index }),
                        width: 360,
                      });
                    }}
                    style={{ marginRight: -4 }}
                  />
                ) : undefined
              }
            >
              <Collapse
                accordion
                defaultActiveKey={["1"]}
                className="awm-inner-steps"
              >
                <Panel
                  key="1"
                  header={
                    <Space size={8}>
                      <span className="awm-step-number">1</span>
                      <Text strong style={{ fontSize: 12 }}>Select Metric</Text>
                    </Space>
                  }
                >
                  <MetricDropdownStep
                    selectedMetricId={slice.selectedMetricId}
                    metricAgg={slice.metricAgg}
                    countSpec={slice.countSpec}
                    onCountSpecChange={(countSpec) =>
                      // A drill subject is silently dropped for a count, so clear any
                      // selection rather than leave one that does nothing.
                      handler(
                        countSpec && slice.selectedDrills?.length
                          ? { countSpec, selectedDrills: [] }
                          : { countSpec },
                      )
                    }
                    onSelectMetric={(id) => {
                      const patch: Partial<typeof slice> = { selectedMetricId: id };
                      if (!id.startsWith("gps-") && slice.showAcwr) patch.showAcwr = false;
                      handler(patch);
                    }}
                    onAggChange={(agg) => handler({ metricAgg: agg })}
                  />
                  {slice.selectedMetricId != null && slice.selectedMetricId.startsWith("gps-") && (
                    <div className="awm-step-body" style={{ marginTop: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Text style={{ fontSize: 12 }}>
                          ACWR
                          <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                            (Acute:Chronic Workload Ratio)
                          </Text>
                        </Text>
                        <Switch
                          size="small"
                          checked={slice.showAcwr}
                          onChange={(checked) => handler({ showAcwr: checked })}
                        />
                      </div>
                    </div>
                  )}
                </Panel>
                <Panel
                  key="2"
                  header={
                    <Space size={8}>
                      <span className="awm-step-number">2</span>
                      <Text strong style={{ fontSize: 12 }}>Date Range</Text>
                    </Space>
                  }
                >
                  <DateRangeStep
                    allowLatestN
                    bindable={isSectionBindable("pie_chart", "date")}
                    value={slice}
                    onChange={handler}
                    itemIndex={index}
                    inheritLabel="Same as Slice 1"
                    description="Set the time window for this slice's data."
                  />
                </Panel>
                <Panel
                  key="3"
                  header={
                    <Space size={8}>
                      <span className="awm-step-number">3</span>
                      <Text strong style={{ fontSize: 12 }}>Filters</Text>
                    </Space>
                  }
                >
                  <PieSubjectPanel
                    slice={slice}
                    onPatch={handler}
                    getSubjectEntities={getSubjectEntities}
                  />
                </Panel>
                <Panel
                  key="4"
                  header={
                    <Space size={8}>
                      <span className="awm-step-number">4</span>
                      <Text strong style={{ fontSize: 12 }}>Additional Options</Text>
                    </Space>
                  }
                >
                  <PieOptionsPanel slice={slice} onPatch={handler} />
                </Panel>
              </Collapse>
            </Panel>
          );
        })}
      </Collapse>

      <Button
        type="text"
        icon={<PlusOutlined />}
        onClick={() => dispatch({ type: "ADD_SLICE" })}
        style={{ width: "100%", fontWeight: 600, color: "#1677ff" }}
      >
        Add Slice
      </Button>
    </>
  );

  const preview = (
    <PiePreviewPanel
      previewData={previewData}
      title={displayTitle}
      hasMetric={previewBuilder.hasValidSlice()}
      showLegend={state.showLegend}
      summary={summaryParts.join(" · ")}
      configuredCount={configuredCount}
      totalSlices={state.slices.length}
      sortBy={state.sortBy}
      onSortChange={(sortBy) => dispatch({ type: "SET_SORT_BY", sortBy })}
    />
  );

  return <ConfigPanelLayout form={form} preview={preview} />;
}
